import { useState } from "react";
import { Link } from "react-router-dom";
import { changeToolLink } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import type { DraftedLink, Tool } from "@/api/types";

// A tool's links to standards and capabilities: the confirmed ones, which put it in a map's
// "Required by a standard" and "Used to deliver this capability" sections, and the ones the
// model drafted, which count only towards "May help" until a person confirms them here.

const HEADING = "mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase";

/** The confirmed links, then any drafted ones with Confirm / Dismiss. Each change sends the
 * tool's `updated_at`, so one made after someone else edited the tool is refused (409) and
 * shown, not overwritten. `onChanged` reloads the tool. */
export function ToolLinks({
  tool: t,
  standardNames,
  capabilityNames,
  onChanged,
}: {
  tool: Tool;
  standardNames: Map<string, string>;
  capabilityNames: Map<string, string>;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Confirms or dismisses one drafted link, then reloads the tool. */
  async function change(action: "confirm" | "dismiss", link: "standard" | "capability", id: string) {
    setError(null);
    setBusy(true);
    try {
      await changeToolLink(t.id, action, link, id, t.updated_at);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.errors.join("; ") : String(e));
    } finally {
      setBusy(false);
    }
  }

  const drafted: { link: "standard" | "capability"; d: DraftedLink; name: string }[] = [
    ...t.drafted_standards.map((d) => ({ link: "standard" as const, d, name: standardNames.get(d.id) ?? d.id })),
    ...t.drafted_capabilities.map((d) => ({ link: "capability" as const, d, name: capabilityNames.get(d.id) ?? d.id })),
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className={HEADING}>Satisfies</h4>
          {t.satisfies_standards.length === 0 ? (
            <p className="text-sm text-text/60">No standard confirmed yet.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {t.satisfies_standards.map((sid) => (
                <li key={sid}>
                  <Link to={`/standards/${encodeURIComponent(sid)}`} className="text-accent underline">
                    {standardNames.get(sid) ?? sid}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h4 className={HEADING}>Supports</h4>
          {t.supports_capabilities.length === 0 ? (
            <p className="text-sm text-text/60">No capability confirmed yet.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {t.supports_capabilities.map((cid) => (
                <li key={cid}>
                  <Link to={`/registry/${encodeURIComponent(cid)}`} className="text-accent underline">
                    {capabilityNames.get(cid) ?? cid}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {drafted.length > 0 && (
        <div>
          <h4 className={HEADING}>Drafted links — awaiting review</h4>
          <p className="mb-1.5 text-xs text-text/50">
            Proposed by the model. Until confirmed they only make this tool a “may help” suggestion.
          </p>
          <ul className="flex flex-col gap-1.5">
            {drafted.map(({ link, d, name }) => (
              <li
                key={`${link}:${d.id}`}
                className="flex items-start justify-between gap-3 rounded border border-line p-2 text-sm"
              >
                <div>
                  <p>
                    <span className="text-xs text-text/50">{link === "standard" ? "Satisfies" : "Supports"}</span>{" "}
                    <Link
                      to={`/${link === "standard" ? "standards" : "registry"}/${encodeURIComponent(d.id)}`}
                      className="text-accent underline"
                    >
                      {name}
                    </Link>
                  </p>
                  <p className="text-text/60">{d.reason}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button onClick={() => change("confirm", link, d.id)} isDisabled={busy}>
                    Confirm
                  </Button>
                  <Button variant="danger" onClick={() => change("dismiss", link, d.id)} isDisabled={busy}>
                    Dismiss
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="text-sm text-warn">{error}</p>}
    </div>
  );
}
